import type { IntlShape } from 'react-intl';
import type { OutreachLiveWarmup, OutreachPlannedPitch } from '../../outreachTypes';
import { clockIn, dayRelation, safeTimeZone } from '../liveFormat';
import { upNextMessages as messages } from './upNextMessages';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * The pitches of one day as the artist sees it: the plan rate, cut to the warm-up cap of the
 * mailbox while it warms up (design s3: "3 a day while the mailbox warms up"). Null while it loads.
 */
export function dayCount(rate: number | null, warmup: OutreachLiveWarmup | null): number | null {
  if (rate === null) {
    return null;
  }
  return warmup !== null && warmup.warming ? Math.min(rate, warmup.todayCap) : rate;
}

/** The people that the next pick takes: the warm-up cap of that day when it grows before the pick. */
export function nextPickCount(
  rate: number | null,
  warmup: OutreachLiveWarmup | null,
  nextBatchAt: string | null
): number | null {
  if (rate === null || warmup === null || !warmup.warming) {
    return rate;
  }
  const grows =
    warmup.nextCap !== null &&
    warmup.nextCapAt !== null &&
    nextBatchAt !== null &&
    Date.parse(warmup.nextCapAt) <= Date.parse(nextBatchAt);
  return Math.min(rate, grows && warmup.nextCap !== null ? warmup.nextCap : warmup.todayCap);
}

/** The time a planned pitch goes out at the earliest: its send time, else its `notBefore`. */
export function plannedAt(pitch: OutreachPlannedPitch): string | null {
  return pitch.sendAt ?? pitch.notBefore;
}

/** "today", "tomorrow" or "on Thu, Oct 30" for a sentence, in the viewer's zone. */
export function dayWord(intl: IntlShape, iso: string | null, now: Date): string {
  if (iso === null) {
    return intl.formatMessage(messages.dayToday);
  }
  const relation = dayRelation(iso, undefined, now);
  if (relation === 'today') {
    return intl.formatMessage(messages.dayToday);
  }
  if (relation === 'tomorrow') {
    return intl.formatMessage(messages.dayTomorrow);
  }
  return intl.formatMessage(messages.dayOn, {
    day: intl.formatDate(new Date(iso), { weekday: 'short', month: 'short', day: 'numeric' }),
  });
}

/** "Today", "Tomorrow" or "Thu" at the start of a time line, in the recipient's zone. */
function dayHead(intl: IntlShape, iso: string, zone: string | undefined, now: Date): string {
  const relation = dayRelation(iso, zone, now);
  if (relation === 'today') {
    return intl.formatMessage(messages.dayTodayCap);
  }
  if (relation === 'tomorrow') {
    return intl.formatMessage(messages.dayTomorrowCap);
  }
  return intl.formatDate(new Date(iso), { weekday: 'short', timeZone: zone });
}

/** "in 19 h", "in 58 min", or a countdown "12:04" under one hour (design s3, s4). */
export function untilText(intl: IntlShape, iso: string, now: Date): string | null {
  const ms = Date.parse(iso) - now.getTime();
  if (ms <= 0) {
    return null;
  }
  if (ms >= HOUR_MS) {
    return intl.formatMessage(messages.inTime, { time: `${Math.round(ms / HOUR_MS)} h` });
  }
  const minutes = Math.floor(ms / MINUTE_MS);
  const seconds = Math.floor((ms % MINUTE_MS) / 1000);
  return intl.formatMessage(messages.inTime, { time: `${minutes}:${String(seconds).padStart(2, '0')}` });
}

/** "14:31:08" to a time, for the next pick (design s4b); null after it. */
export function countdown(iso: string, now: Date): string | null {
  const ms = Date.parse(iso) - now.getTime();
  if (ms <= 0) {
    return null;
  }
  const total = Math.floor(ms / 1000);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${Math.floor(total / 3600)}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

/**
 * The "when" column of a row (design s2-s4): "Tomorrow 10:20 their time" over "09:20 yours ·
 * Germany"; with no send time yet the place over "Tomorrow morning".
 */
export function whenLines(intl: IntlShape, pitch: OutreachPlannedPitch, now: Date): { main: string; sub: string } {
  const { place } = pitch;
  if (pitch.sendAt === null) {
    const earliest = plannedAt(pitch);
    const earliestZone = safeTimeZone(pitch.timeZone);
    const sub =
      earliest === null
        ? intl.formatMessage(messages.whenNoTime)
        : intl.formatMessage(messages.whenFrom, {
            day: dayHead(intl, earliest, earliestZone, now),
            time: clockIn(intl, earliest, earliestZone),
          });
    return { main: place ?? sub, sub: place === null ? '' : sub };
  }
  const zone = safeTimeZone(pitch.timeZone);
  const theirs = clockIn(intl, pitch.sendAt, zone);
  const main =
    pitch.state === 'sending'
      ? intl.formatMessage(messages.whenNow, { time: theirs })
      : intl.formatMessage(messages.whenTheirs, { day: dayHead(intl, pitch.sendAt, zone, now), time: theirs });
  const yours = intl.formatMessage(messages.whenYours, { time: clockIn(intl, pitch.sendAt, undefined) });
  return { main, sub: place === null ? yours : `${yours} · ${place}` };
}

/** The today card title and line while pitches are planned (design s2, s3). Null: the old title fits. */
export function plannedTitle(
  intl: IntlShape,
  pitches: OutreachPlannedPitch[],
  total: number,
  sent: number,
  now: Date
): { title: string; sub: string | null } | null {
  if (total === 0) {
    return null;
  }
  const first = pitches[0];
  const writing = pitches.some((pitch) => pitch.state === 'writing');
  if (writing && pitches.every((pitch) => pitch.sendAt === null)) {
    return {
      title: intl.formatMessage(messages.titleWriting, { count: total }),
      sub: intl.formatMessage(messages.subWriting, { day: dayWord(intl, first ? plannedAt(first) : null, now) }),
    };
  }
  const firstAt = first ? plannedAt(first) : null;
  if (firstAt === null || dayRelation(firstAt, undefined, now) === 'today') {
    return null;
  }
  const sub =
    first?.sendAt === null || first === undefined
      ? null
      : intl.formatMessage(messages.subFirst, {
          theirs: clockIn(intl, first.sendAt, safeTimeZone(first.timeZone)),
          yours: clockIn(intl, first.sendAt, undefined),
        });
  return {
    title: intl.formatMessage(messages.titleLater, { sent, count: total, day: dayWord(intl, firstAt, now) }),
    sub,
  };
}
